/**
 * The entry point: the first code that runs, which connects all the other
 * parts of the game together.
 *
 * It is responsible for:
 * - deciding what's being played: a numbered level, or (only while
 *   developing) a custom level or a level built from an image;
 * - turning mouse clicks and taps into game actions (launching blocks);
 * - running the game loop, which each frame updates the rules, draws the
 *   screen, and plays sounds;
 * - the toolbar in the top-left corner: mute, light/dark theme, and a link to
 *   the how-to-play page (howto.html);
 * - loading the developer tuning panel, in development builds only.
 *
 * The player's level, mute choice and theme choice are saved in
 * `localStorage`: a small key-value store the browser keeps for each website,
 * which survives page reloads.
 */
import type { DevPanel, ImageChoice } from "./dev/panel";
import { type ImageGrid, imageToGrid, type Pixels } from "./game/image";
import { keyAction, START } from "./game/keyboard";
import { columnAt, traySlotAt } from "./game/layout";
import {
	generateLevel,
	type LevelOptions,
	scaledSpeed,
	suggestedMaxCount,
} from "./game/level";
import { createLoop } from "./game/loop";
import {
	describeSelection,
	KEYBOARD_HELP,
	Narrator,
	summarize,
} from "./game/narration";
import type { ThemeName } from "./game/palette";
import {
	generateProgressionLevel,
	type ProgressionLevel,
	tierFor,
} from "./game/progression";
import { DEFAULT_RENDER_SETTINGS, type Hud, Renderer } from "./game/renderer";
import { DEFAULT_SOUND_SETTINGS, SoundEvents, WebSynth } from "./game/sound";
import {
	autoLaunch,
	type GameState,
	launch,
	launchFrom,
	launchFromTray,
	type Source,
	update,
} from "./game/state";
import {
	applyThemeToPage,
	loadTheme,
	otherTheme,
	saveTheme,
} from "./game/theme";

declare global {
	interface Window {
		/** A development-only hook for the browser tests; see below. */
		__rings?: {
			state: () => GameState;
			layout: () => ReturnType<Renderer["layout"]>;
		};
	}
}

const canvas = document.querySelector<HTMLCanvasElement>("#game");
if (!canvas) throw new Error("#game canvas not found");

const renderer = new Renderer(canvas);
const synth = new WebSynth();
const sounds = new SoundEvents(synth);
synth.setVolume(DEFAULT_SOUND_SETTINGS.volume);
setupMute(synth);
setupTheme(renderer);
setupHelpLink();
let panel: DevPanel | null = null;

/**
 * What's being played right now.
 *
 * This is a discriminated union again (see `GameEvent` in state.ts): `kind`
 * says which variant it is. A numbered "progression" level comes with its
 * generated level; a "custom" level (from the dev panel) has its own
 * settings, and optionally a grid made from an image.
 */
type Current =
	| { kind: "progression"; level: ProgressionLevel }
	| {
			kind: "custom";
			options: LevelOptions;
			image?: { name: string; grid: ImageGrid };
	  };

const progress = createProgress();
let current: Current = {
	kind: "progression",
	level: progress.get(progress.saved()),
};
let state: GameState = fresh();

/**
 * A brand-new, unplayed copy of the current level. Used to start a level and
 * to retry it. Generation is repeatable, so the copy is identical.
 */
function fresh(): GameState {
	if (current.kind === "custom" && current.image) {
		return generateLevel(options(), current.image.grid.cells);
	}
	if (current.kind === "progression" && current.level.picture) {
		return generateLevel(options(), current.level.picture.cells); // a picture level
	}
	return generateLevel(options());
}

/** The settings of the level being played. */
function options(): LevelOptions {
	if (current.kind === "progression") return current.level.options;
	const { image } = current;
	// When playing an image, its size decides the grid's size.
	return image
		? { ...current.options, width: image.grid.width, height: image.grid.height }
		: current.options;
}

/**
 * Switch to a numbered level, save it as the player's progress, and start
 * preparing the next level in the background.
 */
function playLevel(level: number): void {
	current = { kind: "progression", level: progress.get(level) };
	progress.save(level);
	state = fresh();
	panel?.setLevelNumber(level);
	panel?.setOptions(options());
	progress.prefetch(level + 1);
}

/** Switch to a custom level with these settings (from the dev panel), keeping any image being used. */
function playCustom(next: LevelOptions): void {
	// If an image is being used, keep building levels around it.
	const image = current.kind === "custom" ? current.image : undefined;
	current = { kind: "custom", options: next, image };
	state = fresh();
	panel?.setOptions(options());
}

/**
 * Switch to building levels from a picture (or back to normal levels, for
 * `null`).
 *
 * `async` marks a function that can wait for slow work (here, loading and
 * decoding the image) using `await`, without freezing the page meanwhile.
 */
async function playImage(choice: ImageChoice | null): Promise<void> {
	if (!choice) {
		playLevel(progress.saved());
		return;
	}
	try {
		const pixels = await loadPixels(choice.source);
		const grid = imageToGrid(pixels, {
			darkCutoff: choice.darkCutoff,
			maxSize: choice.maxSize,
		});
		current = {
			kind: "custom",
			// Block counts suited to the picture's size (see suggestedMaxCount).
			options: {
				...options(),
				maxCount: suggestedMaxCount(grid.width, grid.height),
			},
			image: { name: choice.name, grid },
		};
		state = fresh();
		panel?.setOptions(options());
	} catch (error) {
		alert(
			`Couldn't use that image: ${error instanceof Error ? error.message : error}`,
		);
	}
}

/**
 * Load an image (from a web address, or a file the user picked) and read its
 * pixels. Browsers can only hand over an image's pixels after drawing it on
 * a canvas, so that's what this does, on a hidden canvas.
 */
async function loadPixels(source: string | File): Promise<Pixels> {
	const url = typeof source === "string" ? source : URL.createObjectURL(source);
	try {
		const img = new Image();
		img.src = url;
		await img.decode();
		const canvas = document.createElement("canvas");
		canvas.width = img.naturalWidth;
		canvas.height = img.naturalHeight;
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new Error("2D canvas context not available");
		ctx.drawImage(img, 0, 0);
		return ctx.getImageData(0, 0, canvas.width, canvas.height);
	} finally {
		if (typeof source !== "string") URL.revokeObjectURL(url);
	}
}

/**
 * What a click does once a level has ended: go to the next level after a
 * win, or retry the same level after a loss. Custom levels get a new seed
 * after a win instead.
 */
function advance(): void {
	if (current.kind === "custom") {
		if (state.status === "won")
			current = {
				...current,
				options: { ...current.options, seed: current.options.seed + 1 },
			};
		panel?.setOptions(current.options);
		state = fresh();
	} else if (state.status === "won") {
		playLevel(current.level.level + 1);
	} else {
		state = fresh(); // retry the same level
	}
}

/** The title and end-of-level messages for what's being played. */
function hud(): Hud {
	if (current.kind === "custom") {
		return {
			title: current.image ? `Image · ${current.image.name}` : "Custom",
			won: "Cleared!",
			wonHint: "Click for a new seed",
			lostHint: "Click to retry",
		};
	}
	const { level, picture } = current.level;
	// Picture levels show what the picture is, e.g. "Level 3 · Easy · Heart".
	const name = picture
		? ` · ${picture.name[0]?.toUpperCase()}${picture.name.slice(1)}`
		: "";
	return {
		title: `Level ${level} · ${tierFor(level).name}${name}`,
		won: `Level ${level} cleared!`,
		wonHint: `Click for level ${level + 1}`,
		lostHint: "Click to retry",
	};
}

if (import.meta.env.DEV) {
	const { DevPanel, loadSettings } = await import("./dev/panel");
	const defaults = {
		level: options(),
		render: DEFAULT_RENDER_SETTINGS,
		sound: DEFAULT_SOUND_SETTINGS,
	};
	const settings = loadSettings(defaults);
	settings.level = options(); // the panel shows what's being played
	renderer.configure(settings.render);
	/** Apply new sound settings from the dev panel. */
	const applySound = (sound: typeof DEFAULT_SOUND_SETTINGS) => {
		sounds.settings = sound;
		synth.setVolume(sound.volume);
	};
	applySound(settings.sound);
	panel = new DevPanel(document.body, settings, defaults, {
		onLevelChange: (next) => {
			const prev = options();
			const changed = (Object.keys(next) as (keyof LevelOptions)[]).filter(
				(k) => next[k] !== prev[k],
			);
			if (changed.length === 0) {
				state = fresh(); // same settings: the panel's Restart button
			} else if (changed.every((k) => k === "speed")) {
				// Speed applies live, scaled to the grid like a freshly generated level.
				state.speed = scaledSpeed(
					next.speed,
					state.grid.width,
					state.grid.height,
				);
				if (current.kind === "custom") current.options = next;
			} else {
				playCustom(next);
			}
		},
		onRenderChange: (render) => renderer.configure(render),
		onSoundChange: applySound,
		onLevelNumberChange: playLevel,
		onImage: (choice) => void playImage(choice),
	});
	panel.setLevelNumber(progress.saved());
}

progress.prefetch(progress.saved() + 1);

// ----- Keyboard play and screen readers (see keyboard.ts and narration.ts) -----

// The board is a canvas, so it needs to be made focusable and labelled by
// hand. role="application" tells screen readers to pass key presses straight
// to the game instead of using them to read the page.
canvas.tabIndex = 0;
canvas.setAttribute("role", "application");
canvas.setAttribute("aria-roledescription", "game board");
canvas.setAttribute("aria-label", "Rings game board");
const help = srOnly("p");
help.id = "game-help";
help.textContent = KEYBOARD_HELP;
canvas.setAttribute("aria-describedby", help.id);
/** A "live region": screen readers read out whatever text is put in it. */
const announcer = srOnly("div");
announcer.setAttribute("role", "status");
announcer.setAttribute("aria-live", "polite");
const narrator = new Narrator();
/** The block chosen with the keyboard. Kept when the mouse is used, just not shown. */
let selection: Source | null = null;

/** Show the focus ring (keyboard users) or hide it (mouse users). */
function showSelection(show: boolean): void {
	renderer.setSelection(show ? (selection ?? START) : null);
}

canvas.addEventListener("keydown", (e) => {
	const action = keyAction(e.key, state, selection);
	if (!action) return;
	// Stop the browser's own handling (Space would scroll the page, for example).
	e.preventDefault();
	synth.unlock(); // browsers only allow audio to start from a user gesture
	switch (action.kind) {
		case "select":
			selection = action.selection;
			announce(describeSelection(state, selection));
			break;
		case "launch":
			selection = action.source;
			launchFrom(state, action.source); // the narrator announces what happened
			break;
		case "continue":
			advance();
			break;
		case "summary":
			announce(summarize(state));
			break;
	}
	showSelection(true);
});
canvas.addEventListener("focus", () => {
	// Only show the ring for keyboard focus, not when the board is clicked.
	if (canvas.matches(":focus-visible")) {
		showSelection(true);
		announce(describeSelection(state, selection ?? START));
	}
});
canvas.addEventListener("blur", () => showSelection(false));

/**
 * Put `text` in the live region to be read out. A zero-width space is added
 * or removed each time, so the same message twice still counts as a change
 * and is read again.
 */
function announce(text: string): void {
	const repeat = announcer.textContent?.endsWith("\u200b");
	announcer.textContent = repeat ? text : `${text}\u200b`;
}

/**
 * An element that's hidden on screen but still read by screen readers (see
 * the .sr-only style in style.css).
 */
function srOnly(tag: "p" | "div"): HTMLElement {
	const element = document.createElement(tag);
	element.className = "sr-only";
	// Inside <main>, next to the board it describes.
	(document.querySelector("main") ?? document.body).append(element);
	return element;
}

if (import.meta.env.DEV) {
	// For the browser tests (e2e/): where things are, so they can click blocks
	// on the canvas. Development builds only.
	window.__rings = { state: () => state, layout: () => renderer.layout(state) };
}

canvas.addEventListener("pointerdown", (e) => {
	synth.unlock(); // browsers only allow audio to start from a user gesture
	showSelection(false);
	if (state.status !== "playing") {
		advance();
		return;
	}
	const rect = canvas.getBoundingClientRect();
	const x = e.clientX - rect.left;
	const y = e.clientY - rect.top;
	const layout = renderer.layout(state);
	const slot = traySlotAt(layout, x, y);
	if (slot !== null) {
		launchFromTray(state, slot);
		return;
	}
	const column = columnAt(layout, x, y);
	if (column !== null) launch(state, column);
});

const loop = createLoop({
	update: (dt) => {
		state.launchDelay = renderer.launchDelay(); // blocks hold on the track until their flight lands
		update(state, dt);
		autoLaunch(state); // once every remaining block fits in the tray, the game finishes itself
	},
	render: () => {
		renderer.render(state, hud());
		sounds.sync(state, performance.now());
		const said = narrator.sync(state, hud().title.replaceAll(" · ", ", "));
		if (said.length > 0) announce(said.join(" "));
		panel?.showStats(
			state,
			current.kind === "progression"
				? [
						`level    ${current.level.level} (${tierFor(current.level.level).name})`,
						`bot win  ${Math.round(current.level.winRate * 100)}%`,
					]
				: ["level    custom"],
		);
	},
});

loop.start();

/**
 * Keeps track of numbered levels and the player's saved progress.
 *
 * Generating a level can take a few hundred milliseconds (see
 * progression.ts), so generated levels are kept in a cache (a `Map` from
 * level number to level) and never generated twice. The function returns an
 * object of related functions that share that cache: a lightweight
 * alternative to writing a class.
 */
function createProgress() {
	const KEY = "rings:level";
	const cache = new Map<number, ProgressionLevel>();
	const get = (level: number): ProgressionLevel => {
		let generated = cache.get(level);
		if (!generated) {
			generated = generateProgressionLevel(level);
			cache.set(level, generated);
		}
		return generated;
	};
	return {
		get,
		/**
		 * Generate a level ahead of time, while the browser has nothing else to do
		 * (`requestIdleCallback`), so moving on to it later is instant.
		 */
		prefetch(level: number): void {
			if (cache.has(level)) return;
			const run = () => get(level);
			if ("requestIdleCallback" in window) requestIdleCallback(run);
			else setTimeout(run, 200);
		},
		saved(): number {
			try {
				const level = Number(localStorage.getItem(KEY));
				return Number.isInteger(level) && level >= 1 ? level : 1;
			} catch {
				return 1;
			}
		},
		save(level: number): void {
			try {
				localStorage.setItem(KEY, String(level));
			} catch {
				// Saving can fail (for example in some private browsing modes). That's fine: progress just won't be remembered.
			}
		},
	};
}

/**
 * The toolbar in the top-left corner (created on first use), which holds the
 * mute and theme buttons and the how-to-play link.
 */
function toolbar(): HTMLElement {
	let bar = document.querySelector<HTMLElement>(".toolbar");
	if (!bar) {
		// A <nav> landmark, so screen reader users can jump straight to it.
		bar = document.createElement("nav");
		bar.className = "toolbar";
		bar.setAttribute("aria-label", "Game controls");
		document.body.append(bar);
	}
	return bar;
}

/** A small button added to the toolbar. */
function cornerButton(): HTMLButtonElement {
	const button = document.createElement("button");
	button.type = "button";
	button.className = "corner-button";
	toolbar().append(button);
	return button;
}

/**
 * A "?" link in the toolbar that opens the how-to-play page (howto.html).
 * `BASE_URL` keeps the link right when the game is hosted in a subfolder.
 */
function setupHelpLink(): void {
	const link = document.createElement("a");
	link.className = "corner-button";
	link.href = `${import.meta.env.BASE_URL}howto.html`;
	link.setAttribute("aria-label", "How to play");
	link.title = "How to play";
	link.innerHTML =
		'<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14"/><circle cx="12" cy="17.5" r="0.6" fill="currentColor"/></svg>';
	toolbar().append(link);
}

/**
 * Create the theme button players see. It switches between the dark theme
 * (the default) and the light theme, and the choice is remembered.
 *
 * The button shows and announces what clicking it will do ("Switch to light
 * theme"), which is the usual convention for a two-way toggle like this.
 */
function setupTheme(renderer: Renderer): void {
	const button = cornerButton();
	let theme = loadTheme();

	/** Apply the theme to the game and the page, and update the button to match. */
	const apply = () => {
		renderer.setTheme(theme);
		applyThemeToPage(theme);
		const label = `Switch to ${otherTheme(theme)} theme`;
		button.setAttribute("aria-label", label);
		button.title = label;
		button.innerHTML = themeIcon(otherTheme(theme));
	};
	button.addEventListener("click", () => {
		theme = otherTheme(theme);
		saveTheme(theme);
		apply();
	});
	apply();
}

/** An icon for a theme: a sun for light, a moon for dark. */
function themeIcon(theme: ThemeName): string {
	const shapes = {
		light:
			'<circle cx="12" cy="12" r="4" fill="currentColor"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
		dark: '<path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" fill="currentColor"/>',
	};
	return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${shapes[theme]}</svg>`;
}

/**
 * Create the mute button players see, and the M keyboard shortcut. The
 * choice is remembered between visits.
 *
 * The button uses `aria-label` and `aria-pressed`: attributes that tell
 * screen readers (software that reads the page aloud for blind users) what
 * the button does and whether it's on.
 */
function setupMute(synth: WebSynth): void {
	const KEY = "rings:muted";
	const button = cornerButton();

	let muted = false;
	try {
		muted = localStorage.getItem(KEY) === "1";
	} catch {
		// Reading the saved choice can fail (for example in private browsing). Default to sound on.
	}
	/** Update everything that shows the mute state: the sound itself, the button, and the saved choice. */
	const apply = () => {
		synth.setMuted(muted);
		button.setAttribute("aria-pressed", String(muted));
		button.setAttribute("aria-label", muted ? "Unmute sound" : "Mute sound");
		button.title = `${muted ? "Unmute" : "Mute"} (M)`;
		button.innerHTML = speakerIcon(muted);
		try {
			localStorage.setItem(KEY, muted ? "1" : "0");
		} catch {
			// Saving can fail; remembering the choice is only a convenience.
		}
	};
	const toggle = () => {
		synth.unlock();
		muted = !muted;
		apply();
	};
	button.addEventListener("click", toggle);
	window.addEventListener("keydown", (e) => {
		const typing =
			e.target instanceof HTMLInputElement ||
			e.target instanceof HTMLSelectElement;
		if (!typing && (e.key === "m" || e.key === "M")) toggle();
	});
	apply();
}

/** The button's icon, as inline SVG markup: a speaker with sound waves, or with a cross when muted. */
function speakerIcon(muted: boolean): string {
	const waves = muted
		? '<path d="M16 9l6 6M22 9l-6 6"/>'
		: '<path d="M16 8.5a5 5 0 0 1 0 7M19 5.5a9 9 0 0 1 0 13"/>';
	return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/>${waves}</svg>`;
}
